import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { API_BASE } from './apiConfig';
import { useLinkedPlayers, invalidateLinked } from './api/useLinkedPlayers';
import { useLiveList } from './SocketContext';
import { useTheme } from './ThemeContext';
import {
    Banner, Button, DataTable, DotLabel, ErrorState, Icon, Segmented, Skel, StatusBadge, cx, useToast,
} from './components/ui';
import './ProviderProfilePage.css';
import { gameLabel } from './games';
import { GameCell, playerLabel, startedByLabel } from './sessionAccess';

/* ── helpers (kept local: pages own their formatting) ────────────────── */
const toMs = (x) => {
    if (x == null || x === '') return null;
    if (typeof x === 'number') return x;
    const n = Number(x);
    if (Number.isFinite(n)) return n;
    const p = Date.parse(x);
    return Number.isFinite(p) ? p : null;
};
const sameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
function fmtWhen(ms) {
    if (!ms) return '—';
    const d = new Date(ms);
    const now = new Date();
    const time = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    if (sameDay(d, now)) return `Today, ${time}`;
    const y = new Date(now);
    y.setDate(now.getDate() - 1);
    if (sameDay(d, y)) return `Yesterday, ${time}`;
    return d.toLocaleDateString([], d.getFullYear() === now.getFullYear() ? { day: 'numeric', month: 'short' } : { day: 'numeric', month: 'short', year: 'numeric' });
}
const fmtMonth = (ms) => (ms ? new Date(ms).toLocaleDateString([], { month: 'short', year: 'numeric' }) : '—');
function fmtLength(s) {
    const a = toMs(s.startTime);
    const b = toMs(s.endTime);
    if (!a || !b || b <= a) return '—';
    const sec = Math.round((b - a) / 1000);
    const m = Math.floor(sec / 60);
    return `${m}:${String(sec - m * 60).padStart(2, '0')}`;
}
const greetingFor = (h) => (h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening');
const LINK_LABEL = { sponsorship: 'Sponsored', data: 'Shares session data' };

/** GET /api/sessions — 200 array | 204 empty body (never res.json() a 204). */
async function fetchSessions(authFetch, url) {
    const res = await authFetch(url);
    if (res.status === 204) return [];
    if (!res.ok) throw new Error(`Sessions didn’t load (${res.status})`);
    const text = await res.text();
    if (!text.trim()) return [];
    const data = JSON.parse(text);
    return Array.isArray(data) ? data : [];
}

function ProviderProfilePage() {
    const { authFetch, user } = useAuth();
    const navigate = useNavigate();
    const toast = useToast();
    const { theme, setTheme } = useTheme();
    const linked = useLinkedPlayers();
    const { players, loading: linkLoading, error: linkError, nameOf, reload: reloadLinks } = linked;
    const live = useLiveList();

    // All verified-linked players' sessions in one request (provider, no user_id).
    const [sess, setSess] = useState({ list: [], loading: true, error: null });
    const loadSessions = useCallback(async () => {
        setSess((s) => ({ ...s, loading: true, error: null }));
        try {
            const list = await fetchSessions(authFetch, `${API_BASE}/api/sessions`);
            setSess({ list, loading: false, error: null });
        } catch (e) {
            setSess({ list: [], loading: false, error: e.message || 'Sessions didn’t load' });
        }
    }, [authFetch]);
    useEffect(() => { if (user?.user_id) loadSessions(); }, [user, loadSessions]);

    const [confirm, setConfirm] = useState(null); // link_id awaiting unlink confirmation
    const [busy, setBusy] = useState(null);

    const linkAction = async (p, method) => {
        if (!user?.user_id) return;
        setBusy(p.link_id);
        try {
            const res = await authFetch(`${API_BASE}/api/users/${user.user_id}/linked/${p.link_id}`, { method });
            if (!res.ok) {
                let msg = '';
                try { const j = await res.json(); msg = j.error || j.message || ''; } catch { /* empty body */ }
                throw new Error(msg || `The server returned ${res.status}.`);
            }
            invalidateLinked();
            reloadLinks();
            setConfirm(null);
            toast(method === 'DELETE' ? `Unlinked ${p.name}` : `Verified the link with ${p.name}`, 'success');
        } catch (e) {
            toast(`${method === 'DELETE' ? 'Couldn’t unlink' : 'Couldn’t verify'} ${p.name}. ${e.message}`, 'error');
        } finally {
            setBusy(null);
        }
    };

    // Per-player last session, from the one sessions request.
    const lastByUser = useMemo(() => {
        const m = {};
        sess.list.forEach((s) => {
            const t = toMs(s.startTime);
            if (!s.user_id || !t) return;
            if (!m[s.user_id] || t > m[s.user_id]) m[s.user_id] = t;
        });
        return m;
    }, [sess.list]);

    const sponsored = players.filter((p) => p.link_type === 'sponsorship');
    const awaiting = players.filter((p) => !p.verified);
    const processing = sess.list.filter((s) => s.consolidated && s.consolidated !== 'complete').length;
    const liveCount = live.sessions.length;

    const recent = useMemo(
        () => [...sess.list].filter((s) => toMs(s.startTime)).sort((a, b) => toMs(b.startTime) - toMs(a.startTime)).slice(0, 5),
        [sess.list],
    );

    const firstLoad = linkLoading && players.length === 0 && !linkError;
    const greeting = greetingFor(new Date().getHours());
    const initial = (user?.name || 'P').trim().charAt(0).toUpperCase();

    const now = [
        {
            key: 'live', to: '/live', value: live.loaded ? liveCount : '—', label: liveCount === 1 ? 'Game live now' : 'Games live now',
            dot: liveCount ? 'var(--egdl-danger)' : 'var(--egdl-text-secondary)', pulse: liveCount > 0,
        },
        {
            key: 'proc', to: '/sessions', value: sess.loading || sess.error ? '—' : processing, label: processing === 1 ? 'Session still processing' : 'Sessions still processing',
            dot: processing ? 'var(--egdl-warning)' : 'var(--egdl-text-secondary)',
        },
        {
            key: 'links', to: '/players', value: linkLoading || linkError ? '—' : awaiting.length, label: awaiting.length === 1 ? 'Link awaiting you' : 'Links awaiting you',
            dot: awaiting.length ? 'var(--egdl-warning)' : 'var(--egdl-text-secondary)',
        },
    ];

    const spCols = [
        { key: 'name', label: 'Player', w: 'minmax(0,1.6fr)', strong: true },
        { key: 'email', label: 'Email', w: 'minmax(0,1.6fr)', muted: true },
        { key: 'joined', label: 'Joined', w: 'minmax(0,1fr)' },
        { key: 'last', label: 'Last played', w: 'minmax(0,1fr)' },
        { key: 'status', label: 'Status', w: 'minmax(150px,1fr)', type: 'status' },
    ];
    const spRows = sponsored.map((p) => ({
        id: p.user_id + p.link_type,
        name: p.name,
        email: p.email || '—',
        joined: fmtMonth(toMs(p.since)),
        last: sess.loading ? '…' : fmtWhen(lastByUser[p.user_id]),
        status: p.verified ? 'verified' : 'unverified',
        onClick: () => navigate(`/players/${p.user_id}`),
    }));

    const recentCols = [
        { key: 'player', label: 'Player', w: 'minmax(0,1.4fr)', strong: true },
        {
            key: 'game',
            label: 'Game',
            w: 'minmax(0,1.2fr)',
            render: (r) => <span className="eg-table__text" title={r.game}><GameCell game={r.game} origin={r.origin} /></span>,
        },
        { key: 'when', label: 'Started', w: 'minmax(0,1.2fr)' },
        { key: 'len', label: 'Length', w: 'minmax(0,.7fr)', align: 'right' },
        { key: 'status', label: 'Status', w: 'minmax(150px,1fr)', type: 'status' },
    ];
    const recentRows = recent.map((s) => ({
        id: s._id,
        player: playerLabel(s, nameOf),
        game: s.game ? gameLabel(s.game) : '—',
        origin: startedByLabel(s, user, nameOf),
        when: fmtWhen(toMs(s.startTime)),
        len: fmtLength(s),
        status: live.sessions.some((l) => l.sessionId === s._id) ? 'live' : ['complete', 'pending', 'incomplete'].includes(s.consolidated) ? s.consolidated : null,
        onClick: () => navigate(`/sessions/${s._id}`),
    }));

    // Account facts from what the dashboard already loads, each left out until its data is in:
    // players as on Players (verified = every link verified), and the sessions you ran (guest or started_by you).
    const verifiedBy = new Map();
    players.forEach((p) => verifiedBy.set(p.user_id, (verifiedBy.get(p.user_id) ?? true) && p.verified));
    const ran = sess.list.filter((s) => s.guest || (s.started_by != null && String(s.started_by) === String(user?.user_id)));
    const settings = [
        { label: 'Name', value: user?.name || '—' },
        user?.email ? { label: 'Email', value: user.email } : null,
        { label: 'Account type', value: 'Provider' },
        !linkLoading && !linkError ? { label: 'Linked players', value: `${verifiedBy.size} / ${[...verifiedBy.values()].filter(Boolean).length} verified` } : null,
        !sess.loading && !sess.error ? { label: 'Clinician-run sessions', value: `${ran.length} / ${ran.filter((s) => s.guest).length} guest` } : null,
    ].filter(Boolean);

    return (
        <div className="eg-page pv-page">
            <header className="pv-head">
                <span className="pv-avatar" aria-hidden="true">{initial}</span>
                <div className="pv-head__text">
                    <span className="pv-greet">{greeting}</span>
                    <h1 className="eg-h1">{user?.name || 'Welcome'}</h1>
                    <span className="pv-head__sub">Provider account</span>
                </div>
                <Button variant="primary" size="sm" icon="addPlayer" to="/sponsored-player">Add sponsored player</Button>
            </header>

            {linkError ? (
                <ErrorState title="Your dashboard didn’t load" kicker="" onRetry={reloadLinks} />
            ) : firstLoad ? (
                <div aria-busy="true" className="pv-loading">
                    <span className="eg-sr-only">Loading your dashboard…</span>
                    <Skel h={88} raised />
                    <Skel h={360} raised />
                    <div className="pv-grid"><Skel h={320} raised /><Skel h={320} raised /></div>
                </div>
            ) : (
                <>
                    <section aria-label="Needs attention" className="pv-now">
                        {now.map((n) => (
                            <Link key={n.key} to={n.to} className="pv-now__item">
                                <span className={cx('pv-now__dot', n.pulse && 'eg-dot--pulse')} style={{ background: n.dot }} aria-hidden="true" />
                                <span className="pv-now__text">
                                    <span className="pv-now__value eg-num">{n.value}</span>
                                    <span className="pv-now__label">{n.label}</span>
                                </span>
                                <Icon name="chevronRight" size={20} color="var(--egdl-text-secondary)" />
                            </Link>
                        ))}
                    </section>

                    <section aria-labelledby="pv-sp-h" className="pv-sec">
                        <div className="pv-sec__head">
                            <div className="pv-sec__title">
                                <h2 id="pv-sp-h" className="eg-h2">Sponsorships</h2>
                            </div>
                        </div>
                        <DataTable
                            caption="Sponsorships"
                            columns={spCols}
                            rows={spRows}
                            getKey={(r) => r.id}
                            emptyText="No sponsored players"
                        />
                    </section>

                    <section aria-labelledby="pv-rs-h" className="pv-sec">
                        <div className="pv-sec__head">
                            <div className="pv-sec__title">
                                <h2 id="pv-rs-h" className="eg-h2">Recent sessions</h2>
                            </div>
                            <Button variant="ghost" to="/sessions" icon="sessions">All sessions</Button>
                        </div>
                        {sess.error ? (
                            <Banner tone="danger" title="Sessions didn’t load." action={<Button variant="secondary" onClick={loadSessions}>Retry</Button>} />
                        ) : sess.loading ? (
                            <div aria-busy="true" className="pv-loading pv-loading--rows">
                                <span className="eg-sr-only">Loading sessions…</span>
                                <Skel h={44} raised /><Skel h={56} raised /><Skel h={56} raised /><Skel h={56} raised />
                            </div>
                        ) : (
                            <DataTable
                                caption="Recent sessions"
                                columns={recentCols}
                                rows={recentRows}
                                getKey={(r) => r.id}
                                emptyText="No sessions yet"
                            />
                        )}
                    </section>

                    <div className="pv-grid">
                        <section aria-labelledby="pv-ac-h" className="pv-col">
                            <h2 id="pv-ac-h" className="eg-h2 pv-col__h">Account</h2>
                            {settings.map((r) => (
                                <div key={r.label} className="pv-row">
                                    <span className="pv-row__label">{r.label}</span>
                                    <span className="pv-row__value">{r.value}</span>
                                </div>
                            ))}
                            <div className="pv-row">
                                <span className="pv-row__label">Appearance</span>
                                <Segmented
                                    label="Appearance"
                                    value={theme}
                                    onChange={setTheme}
                                    options={[{ value: 'light', label: 'Light' }, { value: 'dark', label: 'Dark' }]}
                                />
                            </div>
                        </section>

                        <section aria-labelledby="pv-la-h" className="pv-col">
                            <h2 id="pv-la-h" className="eg-h2 pv-col__h">Linked accounts</h2>
                            {players.length === 0 ? (
                                <p className="pv-none">No linked players</p>
                            ) : players.map((p) => {
                                const key = p.link_id || p.user_id + p.link_type;
                                const asking = confirm === key;
                                return (
                                    <div key={key} className="pv-link">
                                        <div className="pv-link__row">
                                            <span className="pv-link__icon" aria-hidden="true"><Icon name={p.link_type === 'sponsorship' ? 'addPlayer' : 'profile'} size={22} /></span>
                                            <span className="pv-link__text">
                                                <Link to={`/players/${p.user_id}`} className="pv-link__name">{p.name}</Link>
                                                <span className="pv-link__meta">{LINK_LABEL[p.link_type] || 'Linked'}{p.email ? ` · ${p.email}` : ''}</span>
                                            </span>
                                            <span className="pv-link__state">
                                                {p.verified
                                                    ? <DotLabel color="var(--egdl-success)" ink="var(--egdl-success-ink)">Linked</DotLabel>
                                                    : <StatusBadge status="unverified" />}
                                            </span>
                                            <span className="pv-link__actions">
                                                {/* The server only verifies data links (POST …/linked/:link_id, no body). */}
                                                {!p.verified && p.link_type === 'data' ? (
                                                    <Button variant="secondary" disabled={busy === p.link_id} onClick={() => linkAction(p, 'POST')} aria-label={`Verify link with ${p.name}`}>
                                                        {busy === p.link_id && !asking ? 'Verifying…' : 'Verify'}
                                                    </Button>
                                                ) : null}
                                                <Button variant="ghost" muted className="pv-unlink" aria-label={`Unlink ${p.name}`} aria-expanded={asking} onClick={() => setConfirm(asking ? null : key)}>
                                                    Unlink
                                                </Button>
                                            </span>
                                        </div>
                                        {asking ? (
                                            <div className="pv-confirm" role="group" aria-label={`Confirm unlinking ${p.name}`}>
                                                <span className="pv-confirm__text">
                                                    Unlink {p.name}? This removes the {p.link_type === 'sponsorship' ? 'sponsorship' : 'data-sharing'} link between you, and you’ll stop seeing their sessions.
                                                </span>
                                                <span className="pv-confirm__btns">
                                                    <Button variant="danger" disabled={busy === p.link_id} onClick={() => linkAction(p, 'DELETE')}>
                                                        {busy === p.link_id ? 'Unlinking…' : 'Unlink'}
                                                    </Button>
                                                    <Button variant="ghost" muted onClick={() => setConfirm(null)}>Cancel</Button>
                                                </span>
                                            </div>
                                        ) : null}
                                    </div>
                                );
                            })}
                        </section>
                    </div>
                </>
            )}
        </div>
    );
}

export default ProviderProfilePage;
